type InitOptions = {
  orgId?: string;
  widgetUrl?: string;
  challengeToken?: string;
  customerToken?: string;
};

type ChatSDK = {
  init: (options?: InitOptions) => void;
  destroy: () => Promise<void>;
};

function generateUUID(): string {
  // Use browser's crypto API for UUID generation
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

function ensureVisitorId(): string {
  const key = 'chat_plugin_visitor_id';
  // Use sessionStorage so each browser tab/window gets its own visitor identity
  const existing = window.sessionStorage.getItem(key);
  if (existing) return existing;
  const id = generateUUID();
  window.sessionStorage.setItem(key, id);
  return id;
}

function resolveBaseUrl(): string {
  const current = document.currentScript as HTMLScriptElement | null;
  if (current?.src) {
    try {
      const url = new URL('.', current.src);
      return `${url.origin}${url.pathname.replace(/\/$/, '')}`;
    } catch (err) {
      console.warn('Unable to parse script src', err);
    }
  }
  return window.location.origin;
}

function createStyles() {
  if (document.getElementById('chat-plugin-sdk-styles')) return;
  const style = document.createElement('style');
  style.id = 'chat-plugin-sdk-styles';
  style.textContent = `
    .chat-plugin-launcher { position: fixed; bottom: 20px; right: 20px; background: #111827; color: #fff; border-radius: 9999px; padding: 12px 16px; cursor: pointer; box-shadow: 0 10px 25px rgba(0,0,0,0.2); z-index: 2147483000; }
    .chat-plugin-launcher:hover { background: #0f172a; }
    .chat-plugin-iframe { position: fixed; bottom: 80px; right: 20px; width: 360px; height: 520px; border: none; border-radius: 16px; box-shadow: 0 20px 40px rgba(0,0,0,0.25); z-index: 2147483000; display: none; }
    @media(max-width: 480px) { .chat-plugin-iframe { width: 90vw; height: 70vh; right: 5vw; } }
  `;
  document.head.appendChild(style);
}

function buildIframe(src: string): HTMLIFrameElement {
  const iframe = document.createElement('iframe');
  iframe.src = src;
  iframe.className = 'chat-plugin-iframe';
  iframe.allow = 'clipboard-write; autoplay';
  iframe.title = 'Chat Widget';
  return iframe;
}

function buildLauncher(): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'chat-plugin-launcher';
  btn.innerText = 'Chat with us';
  return btn;
}

let disposeWidget: (() => Promise<void>) | undefined;

async function destroy() {
  const dispose = disposeWidget;
  disposeWidget = undefined;
  await dispose?.();
}

function init(options: InitOptions = {}) {
  void destroy();
  const visitorId = ensureVisitorId();
  const base = options.widgetUrl ?? resolveBaseUrl();
  const orgId = options.orgId ?? (document.currentScript as HTMLScriptElement | null)?.dataset.orgId;
  const challengeToken = options.challengeToken ?? (document.currentScript as HTMLScriptElement | null)?.dataset.challengeToken;
  const customerToken = options.customerToken ?? (document.currentScript as HTMLScriptElement | null)?.dataset.customerToken;
  if (!orgId) {
    console.error('ChatPluginSDK: orgId is required');
    return;
  }

  createStyles();
  const query = new URLSearchParams({
    orgId,
    visitorId
  });
  query.set('parentOrigin', window.location.origin);
  query.set('sdkLoadedAt', Date.now().toString());
  const iframeSrc = `${base}/?${query.toString()}`;
  const iframe = buildIframe(iframeSrc);
  const launcher = buildLauncher();
  const widgetOrigin = new URL(iframeSrc, window.location.href).origin;
  let resolveLogout: (() => void) | undefined;
  const receive = (event: MessageEvent) => {
    if (event.origin !== widgetOrigin || event.source !== iframe.contentWindow) return;
    if (event.data?.type === 'chat:ready') {
      iframe.contentWindow?.postMessage({ type: 'chat:identity', customerToken, challengeToken }, widgetOrigin);
    }
    if (event.data?.type === 'chat:logged-out') resolveLogout?.();
  };
  window.addEventListener('message', receive);
  document.body.append(iframe, launcher);
  disposeWidget = async () => {
    iframe.style.display = 'none';
    launcher.remove();
    await new Promise<void>((resolve) => {
      resolveLogout = resolve;
      iframe.contentWindow?.postMessage({ type: 'chat:logout' }, widgetOrigin);
      setTimeout(resolve, 1500);
    });
    window.removeEventListener('message', receive);
    iframe.remove();
    launcher.remove();
  };

  let open = false;
  const toggle = () => {
    open = !open;
    iframe.style.display = open ? 'block' : 'none';
    launcher.innerText = open ? 'Close chat' : 'Chat with us';
  };

  launcher.addEventListener('click', toggle);
}

const api: ChatSDK = { init, destroy };

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
if (typeof window !== 'undefined') window.ChatPluginSDK = api;

const autoScript = document.currentScript as HTMLScriptElement | null;
if (autoScript?.dataset.orgId) {
  init({ orgId: autoScript.dataset.orgId });
}

export { init, destroy };
